import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

interface SponsorSlot {
  id: string;
  tier: string;
  sponsor_name: string | null;
  sponsor_url: string | null;
  is_occupied: boolean;
  display_order: number;
}

const tierLabels: Record<string, string> = {
  select: "Select Partner",
  preferred: "Preferred Partner",
  anchor: "Anchor Partner",
};

interface SponsorSlotsProps {
  variant?: "sidebar" | "footer";
}

const SponsorSlots = ({ variant = "footer" }: SponsorSlotsProps) => {
  const [slots, setSlots] = useState<SponsorSlot[]>([]);

  useEffect(() => {
    const fetchSlots = async () => {
      const { data } = await supabase
        .from("sponsor_slots")
        .select("*")
        .order("display_order");
      if (data) setSlots(data as SponsorSlot[]);
    };
    fetchSlots();
  }, []);

  if (slots.length === 0) return null;

  const isSidebar = variant === "sidebar";

  return (
    <div className={isSidebar ? "" : "mb-10 pb-8 border-b border-border/15"}>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className={
          isSidebar
            ? "font-body text-[7px] uppercase tracking-[0.2em] text-accent/60 mb-2.5"
            : "font-body text-[8px] uppercase tracking-[0.2em] text-accent/50 mb-4 text-center"
        }
      >
        Strategic Venue Partners
      </motion.p>

      <div
        className={
          isSidebar
            ? "flex flex-col gap-1.5"
            : "flex justify-center items-center gap-8"
        }
      >
        {slots.map((slot, i) => {
          const label = tierLabels[slot.tier] || slot.tier;
          const displayName = slot.is_occupied && slot.sponsor_name
            ? slot.sponsor_name
            : label;

          const content = (
            <motion.span
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              whileHover={{ scale: 1.04 }}
              className={
                isSidebar
                  ? "group relative font-body text-[8px] uppercase tracking-[0.12em] transition-all duration-300 cursor-pointer block py-0.5 " +
                    (slot.is_occupied
                      ? "text-accent/70 hover:text-accent"
                      : "text-accent/30 hover:text-accent/55")
                  : "group relative font-body text-[9px] uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer " +
                    (slot.is_occupied
                      ? "text-accent/70 hover:text-accent"
                      : "text-accent/25 hover:text-accent/50")
              }
            >
              {displayName}
              <span
                className={
                  "absolute bottom-0 left-0 h-px bg-accent/40 transition-all duration-300 " +
                  "w-0 group-hover:w-full"
                }
              />
            </motion.span>
          );

          if (slot.is_occupied && slot.sponsor_url) {
            return (
              <a
                key={slot.id}
                href={slot.sponsor_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {content}
              </a>
            );
          }

          return (
            <Link key={slot.id} to="/partner/onboarding">
              {content}
            </Link>
          );
        })}
      </div>

      {!isSidebar && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="font-body text-[9px] text-accent/30 text-center mt-2.5"
        >
          <a
            href="mailto:hello@venuevine.co.uk?subject=Strategic%20Partnership"
            className="hover:text-accent transition-colors duration-300"
          >
            Partnership enquiries
          </a>
        </motion.p>
      )}
    </div>
  );
};

export default SponsorSlots;
